/**
 * Track point encoding — versioned varint zigzag deltas (spec 03 / reused by
 * sync spec 08). ~6–8 bytes/point vs ~50 for JSON. Byte 0 is the format version.
 *
 * Per point, four zigzag-varint deltas from the previous point:
 *   lat*1e5, lon*1e5, dtSeconds (cumulative gap), sogKn*10
 * The first point's deltas are taken from zero (absolute). Absolute wall-clock
 * start time lives in trackMeta.startedAt; dtSeconds reconstructs each ts.
 */
import { EARTH_RADIUS_M } from './geo.js';

export const POLYLINE_VERSION = 1;

export interface TrackPoint {
  lat: number;
  lon: number;
  /** epoch ms */
  ts: number;
  /** knots; null → stored as 0 */
  sogKn: number | null;
}

function zigzag(n: number): number {
  return (n << 1) ^ (n >> 31);
}

function unzigzag(n: number): number {
  return (n >>> 1) ^ -(n & 1);
}

function pushVarint(out: number[], value: number): void {
  let v = value >>> 0;
  while (v >= 0x80) {
    out.push((v & 0x7f) | 0x80);
    v >>>= 7;
  }
  out.push(v);
}

function readVarint(buf: Uint8Array, pos: number): [number, number] {
  let result = 0;
  let shift = 0;
  let p = pos;
  for (;;) {
    const byte = buf[p]!;
    p += 1;
    result |= (byte & 0x7f) << shift;
    if ((byte & 0x80) === 0) break;
    shift += 7;
  }
  return [result >>> 0, p];
}

export function encodeTrack(points: TrackPoint[]): Uint8Array {
  const out: number[] = [POLYLINE_VERSION];
  let prevLat = 0;
  let prevLon = 0;
  let prevTs = 0;
  let prevSog = 0;

  for (const p of points) {
    const lat = Math.round(p.lat * 1e5);
    const lon = Math.round(p.lon * 1e5);
    const tsS = Math.round(p.ts / 1000);
    const sog = Math.round((p.sogKn ?? 0) * 10);

    pushVarint(out, zigzag(lat - prevLat));
    pushVarint(out, zigzag(lon - prevLon));
    pushVarint(out, zigzag(tsS - prevTs));
    pushVarint(out, zigzag(sog - prevSog));

    prevLat = lat;
    prevLon = lon;
    prevTs = tsS;
    prevSog = sog;
  }

  return Uint8Array.from(out);
}

export function decodeTrack(buf: Uint8Array): TrackPoint[] {
  if (buf.length === 0) return [];
  const version = buf[0];
  if (version !== POLYLINE_VERSION) {
    throw new Error(`Unsupported track encoding version ${version}`);
  }

  const points: TrackPoint[] = [];
  let pos = 1;
  let lat = 0;
  let lon = 0;
  let tsS = 0;
  let sog = 0;

  while (pos < buf.length) {
    let d: number;
    [d, pos] = readVarint(buf, pos);
    lat += unzigzag(d);
    [d, pos] = readVarint(buf, pos);
    lon += unzigzag(d);
    [d, pos] = readVarint(buf, pos);
    tsS += unzigzag(d);
    [d, pos] = readVarint(buf, pos);
    sog += unzigzag(d);

    points.push({ lat: lat / 1e5, lon: lon / 1e5, ts: tsS * 1000, sogKn: sog / 10 });
  }

  return points;
}

// --- Decimation (at write time, spec 03) ---

export const DECIMATE_MIN_METERS = 10;
export const DECIMATE_MIN_COG_DEG = 5;
export const DECIMATE_MIN_SECONDS = 30;

export interface DecimationCandidate {
  lat: number;
  lon: number;
  ts: number;
  cogDeg: number | null;
}

/**
 * Keep a fix iff it moved ≥10 m, turned ≥5°, or ≥30 s elapsed since the last
 * kept fix. `last` null means this is the first point of a trip (always kept).
 */
export function shouldKeepPoint(
  last: DecimationCandidate | null,
  next: DecimationCandidate,
  distMeters: number,
): boolean {
  if (!last) return true;
  if (distMeters >= DECIMATE_MIN_METERS) return true;
  if (next.ts - last.ts >= DECIMATE_MIN_SECONDS * 1000) return true;
  if (last.cogDeg != null && next.cogDeg != null) {
    let diff = Math.abs(next.cogDeg - last.cogDeg) % 360;
    if (diff > 180) diff = 360 - diff;
    if (diff >= DECIMATE_MIN_COG_DEG) return true;
  }
  return false;
}

// --- Douglas–Peucker simplification (server-side, spec 15 trip sharing) ---

interface LatLon {
  lat: number;
  lon: number;
}

const DEG_TO_M_LAT = (EARTH_RADIUS_M * Math.PI) / 180;

/** Perpendicular distance (metres) from `p` to the segment `a`–`b`, using a
 *  local equirectangular projection centred on the segment — accurate enough
 *  for display simplification over the short legs a single trip covers. */
function perpendicularDistanceMeters(p: LatLon, a: LatLon, b: LatLon): number {
  const latRef = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  const mPerDegLon = DEG_TO_M_LAT * Math.cos(latRef);

  const ax = 0;
  const ay = 0;
  const bx = (b.lon - a.lon) * mPerDegLon;
  const by = (b.lat - a.lat) * DEG_TO_M_LAT;
  const px = (p.lon - a.lon) * mPerDegLon;
  const py = (p.lat - a.lat) * DEG_TO_M_LAT;

  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);

  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

/** Classic Douglas–Peucker: keep points whose deviation from the chord they'd
 *  be skipped by exceeds `epsilonM`. Always keeps the first and last point.
 *  Iterative (explicit stack) so pathological inputs can't blow the call stack. */
function douglasPeucker<T extends LatLon>(points: T[], epsilonM: number): T[] {
  const n = points.length;
  if (n < 3) return points;

  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;

  const stack: Array<[number, number]> = [[0, n - 1]];
  while (stack.length > 0) {
    const [start, end] = stack.pop()!;
    if (end <= start + 1) continue;

    let maxDist = -1;
    let maxIdx = -1;
    for (let i = start + 1; i < end; i++) {
      const d = perpendicularDistanceMeters(points[i]!, points[start]!, points[end]!);
      if (d > maxDist) {
        maxDist = d;
        maxIdx = i;
      }
    }

    if (maxDist > epsilonM && maxIdx !== -1) {
      keep[maxIdx] = 1;
      stack.push([start, maxIdx], [maxIdx, end]);
    }
  }

  const out: T[] = [];
  for (let i = 0; i < n; i++) {
    if (keep[i]) out.push(points[i]!);
  }
  return out;
}

/**
 * Simplify a track to at most `maxPoints`, preserving shape (spec 15's server-
 * side track for the share viewer, ≤ 500 points). Binary-searches the DP
 * epsilon since a fixed epsilon can't target an exact output size.
 */
export function simplifyTrack<T extends LatLon>(points: T[], maxPoints: number): T[] {
  if (maxPoints < 2) maxPoints = 2;
  if (points.length <= maxPoints) return points;

  let lo = 0;
  let hi = 100_000; // metres; large enough to collapse any realistic trip to 2 points
  let best = douglasPeucker(points, hi);

  for (let i = 0; i < 24 && hi - lo > 1e-6; i++) {
    const mid = (lo + hi) / 2;
    const candidate = douglasPeucker(points, mid);
    if (candidate.length <= maxPoints) {
      best = candidate;
      hi = mid;
    } else {
      lo = mid;
    }
  }

  return best;
}
